import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Paper,
  InputAdornment,
  CircularProgress,
  Alert
} from '@mui/material';
import { Search, Work } from '@mui/icons-material';
import { bploService } from '../../../services/bploService';

const OccupationSelector = ({ value = [], onChange, error, helperText }) => {
  const [occupations, setOccupations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    loadOccupations();
  }, []);

  const loadOccupations = async () => {
    setLoading(true);
    try {
      const { success, data, error } = await bploService.getOccupations();
      if (success) {
        setOccupations(data || []);
      } else {
        setFetchError(error || 'Failed to load occupations');
      }
    } catch (err) {
      setFetchError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const filteredOccupations = useMemo(() => {
    if (!search.trim()) return occupations;
    const s = search.toLowerCase();
    return occupations.filter(o => 
      o.name.toLowerCase().includes(s)
    );
  }, [occupations, search]);

  const handleToggle = (occupation) => {
    const currentIndex = value.findIndex(item => item.id === occupation.id);
    const newChecked = [...value];

    if (currentIndex === -1) {
      newChecked.push({
        id: occupation.id,
        name: occupation.name,
        fees: occupation.fees // Store fees for calculation if needed
      });
    } else {
      newChecked.splice(currentIndex, 1);
    }

    onChange(newChecked);
  };

  if (loading) return <CircularProgress size={24} />;
  if (fetchError) return <Alert severity="error">{fetchError}</Alert>;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
        Select Occupations / Callings
      </Typography>
      
      {/* Selected Chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
        {value.map((occ) => (
          <Chip
            key={occ.id}
            label={occ.name}
            onDelete={() => handleToggle(occ)}
            color="primary"
            size="small"
            icon={<Work sx={{ fontSize: '16px !important' }} />}
          />
        ))}
        {value.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No occupations selected
          </Typography>
        )}
      </Box>

      {/* Search and List */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search occupations..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
        }}
        error={Boolean(error)}
        helperText={helperText || ''}
        sx={{ mb: 1 }}
      />

      <Paper variant="outlined" sx={{ maxHeight: 250, overflow: 'auto', bgcolor: 'background.paper' }}>
        <List dense disablePadding>
          {filteredOccupations.length === 0 ? (
            <ListItem>
              <ListItemText primary="No occupations found" sx={{ color: 'text.secondary', textAlign: 'center' }} />
            </ListItem>
          ) : (
            filteredOccupations.map((occupation) => {
              const labelId = `checkbox-list-label-${occupation.id}`;
              const isChecked = value.some(item => item.id === occupation.id);

              return (
                <ListItem
                  key={occupation.id}
                  disablePadding
                >
                  <ListItemButton role={undefined} onClick={() => handleToggle(occupation)} dense>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={isChecked}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </ListItemIcon>
                    <ListItemText 
                      id={labelId} 
                      primary={occupation.name} 
                      // secondary={`Fees: ${JSON.stringify(occupation.fees)}`} // Simplify display
                    />
                  </ListItemButton>
                </ListItem>
              );
            })
          )}
        </List>
      </Paper>
    </Box>
  );
};

export default OccupationSelector;
